import React from 'react';

interface FatigueGaugeProps {
  value: number; // 1-7 scale
  size?: number;
}

export const FatigueGauge: React.FC<FatigueGaugeProps> = ({ value, size = 200 }) => {
  const radius = (size - 40) / 2;
  const centerX = size / 2;
  const centerY = size / 2;
  const strokeWidth = 12;
  
  // Normalize value to 0-1 scale, then to 180 degrees
  const normalizedValue = Math.max(0, Math.min(7, value)) / 7;
  const valueAngle = normalizedValue * 180;
  
  // Create arc path helper
  const createArc = (startAngle: number, endAngle: number) => {
    // Convert to radians and adjust for semicircle (starts from left, goes to right)
    const startRad = ((startAngle + 180) * Math.PI) / 180;
    const endRad = ((endAngle + 180) * Math.PI) / 180;
    
    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);
    
    const largeArcFlag = endAngle - startAngle > 90 ? 1 : 0;
    
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
  };
  
  // Calculate needle position
  const needleAngle = (valueAngle + 180) * Math.PI / 180;
  const needleLength = radius - 15;
  const needleX = centerX + needleLength * Math.cos(needleAngle);
  const needleY = centerY + needleLength * Math.sin(needleAngle);
  
  // Color segments for fatigue levels
  // Green for 1-2 (low fatigue), Orange for 3-5, Red for 6-7 (high fatigue)
  // 0-60°: Green (values 1-2), 60-120°: Orange (values 3-5), 120-180°: Red (values 6-7)
  
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.65} className="overflow-visible">
        {/* Background arcs for fatigue levels */}
        {/* Green segment (0-60 degrees) - Low fatigue (1-2) */}
        <path
          d={createArc(0, 60)}
          fill="none"
          stroke="rgb(16, 185, 129)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity={0.3}
        />
        
        {/* Orange segment (60-120 degrees) - Medium fatigue (3-5) */}
        <path
          d={createArc(60, 120)}
          fill="none"
          stroke="rgb(245, 158, 11)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity={0.3}
        />
        
        {/* Red segment (120-180 degrees) - High fatigue (6-7) */}
        <path
          d={createArc(120, 180)}
          fill="none"
          stroke="rgb(239, 68, 68)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity={0.3}
        />
        
        {/* Active arcs up to current value */}
        {valueAngle > 0 && (
          <path
            d={createArc(0, Math.min(valueAngle, 60))}
            fill="none"
            stroke="rgb(16, 185, 129)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        )}
        
        {valueAngle > 60 && (
          <path
            d={createArc(60, Math.min(valueAngle, 120))}
            fill="none"
            stroke="rgb(245, 158, 11)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        )}
        
        {valueAngle > 120 && (
          <path
            d={createArc(120, Math.min(valueAngle, 180))}
            fill="none"
            stroke="rgb(239, 68, 68)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        )}
        
        {/* Scale markers */}
        {[0, 30, 60, 90, 120, 150, 180].map((angle) => {
          const markerRad = ((angle + 180) * Math.PI) / 180;
          const innerRadius = radius - 15;
          const outerRadius = radius - 5;
          
          const x1 = centerX + innerRadius * Math.cos(markerRad);
          const y1 = centerY + innerRadius * Math.sin(markerRad);
          const x2 = centerX + outerRadius * Math.cos(markerRad);
          const y2 = centerY + outerRadius * Math.sin(markerRad);
          
          return (
            <line
              key={angle}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#6B7280"
              strokeWidth={2}
            />
          );
        })}
        
        {/* Scale labels */}
        {[
          { value: 1, angle: 0 },
          { value: 4, angle: 90 },
          { value: 7, angle: 180 }
        ].map(({ value: labelValue, angle }) => {
          const labelRad = ((angle + 180) * Math.PI) / 180;
          const labelRadius = radius + 20;
          const labelX = centerX + labelRadius * Math.cos(labelRad);
          const labelY = centerY + labelRadius * Math.sin(labelRad) + 5;
          
          return (
            <text
              key={labelValue}
              x={labelX}
              y={labelY}
              textAnchor="middle"
              className="text-sm fill-gray-600 font-medium"
            >
              {labelValue}
            </text>
          );
        })}
        
        {/* Center dot */}
        <circle
          cx={centerX}
          cy={centerY}
          r={8}
          fill="#374151"
        />
        
        {/* Needle */}
        <line
          x1={centerX}
          y1={centerY}
          x2={needleX}
          y2={needleY}
          stroke="#374151"
          strokeWidth={3}
          strokeLinecap="round"
        />
        
        {/* Small circle at needle end */}
        <circle
          cx={needleX}
          cy={needleY}
          r={3}
          fill="#374151"
        />
      </svg>
      
      {/* Value display */}
      <div className="text-center mt-2">
        <div className="text-2xl font-bold">{value}</div>
        <div className="text-sm text-muted-foreground">από 7</div>
      </div>
    </div>
  );
};